/* eslint-disable @typescript-eslint/no-explicit-any */
import { inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { CategoryType } from '../consent/category-type.enum';
import { ConsentBasedTrackerService } from '../consent/consent-based-tracker.service';
import { MatomoLoaderService } from './matomo-loader.service';

enum EventTrackerCategories {
  LOGIN = 'log-in',
}

enum ExecType {
  TRACK_EVENT = 'trackEvent',
}

@Injectable({ providedIn: 'root' })
export class EventTrackerService extends ConsentBasedTrackerService {
  private readonly matomoLoader = inject(MatomoLoaderService);
  private readonly router = inject(Router);
  private routerSubscription: Subscription | null = null;
  private lastPageUrl = '';

  constructor() {
    super(CategoryType.ANALYTICS);
  }

  private get execArray(): any[] {
    return (globalThis as any)._paq || [];
  }

  protected enable(): void {
    this.matomoLoader.init().then(() => {
      this.execArray.push(['rememberConsentGiven']);
      this.enabled = true;
      this.collectEvents = false;
      this.queue.flush();
      this.startPageTracking();
    });
  }

  protected disable(): void {
    this.stopPageTracking();
    this.execArray.push(['forgetConsentGiven']);
    this.enabled = false;
    this.collectEvents = false;
    this.queue.clear();
  }

  private startPageTracking(): void {
    this.trackPageView();
    this.routerSubscription = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => this.trackPageView());
  }

  private stopPageTracking(): void {
    this.routerSubscription?.unsubscribe();
    this.routerSubscription = null;
  }

  private trackPageView(): void {
    const currentUrl = location.href;
    if (this.lastPageUrl) {
      this.execArray.push(['setReferrerUrl', this.lastPageUrl]);
    }
    this.execArray.push(['setCustomUrl', currentUrl]);
    this.execArray.push(['setDocumentTitle', document.title]);
    this.execArray.push(['trackPageView']);
    this.lastPageUrl = currentUrl;
  }

  trackLogin(customerName: string): void {
    this.handleEvent<[ExecType, EventTrackerCategories, string]>(
      [ExecType.TRACK_EVENT, EventTrackerCategories.LOGIN, customerName],
      (eventPayload) => this.originalTrackEvent(eventPayload),
    );
  }

  private originalTrackEvent([method, category, action]: [ExecType, EventTrackerCategories, string]): void {
    this.execArray.push([method, category, action]);
  }
}
